import { getCategory, pickPuzzle as defaultPickPuzzle } from './categories';
import {
  AvailableActions,
  EngineDeps,
  GameAction,
  GameState,
  MAX_PLAYERS,
  MAX_ROUNDS,
  MIN_CASH_SPIN,
  MIN_ROUNDS,
  Player,
  SLOT_SPECIALS,
  SpecialReel,
  SpinResult,
  VOWEL_COST,
  VOWELS,
} from './types';

export class GameError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GameError';
  }
}

export function isVowel(letter: string): boolean {
  return (VOWELS as readonly string[]).includes(letter.toLowerCase());
}

export function isConsonant(letter: string): boolean {
  const normalized = letter.toLowerCase();
  return /^[a-z]$/.test(normalized) && !isVowel(normalized);
}

export function normalizeLetter(letter: string): string {
  const normalized = letter.trim().toLowerCase();
  if (!/^[a-z]$/.test(normalized)) {
    throw new GameError('Guess a single letter A–Z');
  }
  return normalized;
}

export function uniqueLettersInPhrase(phrase: string): string[] {
  const letters = new Set<string>();
  for (const char of phrase.toLowerCase()) {
    if (/[a-z]/.test(char)) {
      letters.add(char);
    }
  }
  return Array.from(letters);
}

export function countLetterInPhrase(phrase: string, letter: string): number {
  const target = letter.toLowerCase();
  let count = 0;
  for (const char of phrase.toLowerCase()) {
    if (char === target) {
      count += 1;
    }
  }
  return count;
}

export function isBoardComplete(phrase: string, guessedLetters: string[]): boolean {
  const guessed = new Set(guessedLetters.map((letter) => letter.toLowerCase()));
  return uniqueLettersInPhrase(phrase).every((letter) => guessed.has(letter));
}

export function normalizeSolveGuess(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, ' ');
}

export function generateSpin(random: () => number = Math.random): SpinResult {
  const reelValues: [number, number, number, number] = [
    Math.floor(random() * 10),
    Math.floor(random() * 10),
    Math.floor(random() * 10),
    Math.floor(random() * 10),
  ];
  const specialValue = SLOT_SPECIALS[Math.floor(random() * SLOT_SPECIALS.length)] as SpecialReel;
  const rawValue = reelValues.reduce((total, digit) => total * 10 + digit, 0);
  return {
    reelValues,
    specialValue,
    cashValue: Math.max(MIN_CASH_SPIN, rawValue),
  };
}

export function isValidSpin(spin: SpinResult): boolean {
  if (!spin || !Array.isArray(spin.reelValues) || spin.reelValues.length !== 4) {
    return false;
  }
  if (spin.reelValues.some((digit) => !Number.isInteger(digit) || digit < 0 || digit > 9)) {
    return false;
  }
  if (!(SLOT_SPECIALS as readonly string[]).includes(spin.specialValue)) {
    return false;
  }
  return Number.isInteger(spin.cashValue) && spin.cashValue >= MIN_CASH_SPIN;
}

export function currentPlayer(state: GameState): Player {
  const player = state.players[state.currentPlayerIndex];
  if (!player) {
    throw new GameError('No current player');
  }
  return player;
}

export function getAvailableActions(state: GameState, playerId: string): AvailableActions {
  const me = state.players.find((player) => player.id === playerId);
  const isTurn = Boolean(me) && currentPlayerSafe(state)?.id === playerId;
  const playing = state.status === 'playing';

  return {
    isMyTurn: isTurn,
    isHost: Boolean(me?.isHost),
    canSpin: playing && isTurn && state.phase === 'awaiting_action',
    canGuessConsonant: playing && isTurn && state.phase === 'awaiting_consonant',
    canBuyVowel: playing && isTurn && state.phase === 'awaiting_action' && (me?.roundScore ?? 0) >= VOWEL_COST,
    canSolve: playing && isTurn && state.phase === 'awaiting_action',
    canEndGame: Boolean(me?.isHost) && state.status !== 'finished',
    canAdvance: playing && state.phase === 'round_complete',
  };
}

function currentPlayerSafe(state: GameState): Player | undefined {
  return state.players[state.currentPlayerIndex];
}

export function createInitialGame(params: {
  id: string;
  hostId: string;
  hostName: string;
  now?: number;
}): GameState {
  const now = params.now ?? Date.now();
  const hostName = params.hostName.trim() || 'Host';

  return {
    id: params.id,
    hostId: params.hostId,
    playerIds: [params.hostId],
    players: [
      {
        id: params.hostId,
        name: hostName,
        roundScore: 0,
        totalScore: 0,
        isHost: true,
      },
    ],
    status: 'lobby',
    phase: 'lobby',
    totalRounds: 3,
    currentRound: 0,
    selectedCategoryIds: [],
    usedPuzzles: [],
    currentPhrase: '',
    currentCategory: '',
    currentPlayerIndex: 0,
    currentSpinValue: null,
    lastSpin: null,
    lastSpinAt: null,
    guessedLetters: [],
    message: `${hostName} created the game. Share the code so others can join.`,
    winners: [],
    createdAt: now,
    updatedAt: now,
  };
}

export function applyAction(state: GameState, action: GameAction, deps: EngineDeps = {}): GameState {
  const now = deps.now ?? Date.now();

  switch (action.type) {
    case 'JOIN':
      return touch(joinGame(state, action.playerId, action.playerName), now);
    case 'SET_CONFIG':
      return touch(setConfig(state, action.playerId, action.totalRounds, action.selectedCategoryIds), now);
    case 'START':
      return touch(startGame(state, action.playerId, deps), now);
    case 'SPIN':
      return touch(spinSlot(state, action.playerId, action.spin, now), now);
    case 'GUESS_CONSONANT':
      return touch(guessConsonant(state, action.playerId, action.letter), now);
    case 'BUY_VOWEL':
      return touch(buyVowel(state, action.playerId, action.letter), now);
    case 'SOLVE':
      return touch(solvePhrase(state, action.playerId, action.guess), now);
    case 'ADVANCE_ROUND':
      return touch(advanceRound(state, deps), now);
    case 'END_GAME':
      return touch(endGame(state, action.playerId), now);
    default:
      throw new GameError('Unknown action');
  }
}

function touch(state: GameState, now: number): GameState {
  return { ...state, updatedAt: now };
}

function requireHost(state: GameState, playerId: string): void {
  const player = state.players.find((entry) => entry.id === playerId);
  if (!player?.isHost) {
    throw new GameError('Only the host can do that');
  }
}

function requireTurn(state: GameState, playerId: string): Player {
  if (state.status !== 'playing') {
    throw new GameError('The game is not in progress');
  }
  const player = currentPlayer(state);
  if (player.id !== playerId) {
    throw new GameError("It's not your turn");
  }
  return player;
}

function clonePlayers(players: Player[]): Player[] {
  return players.map((player) => ({ ...player }));
}

function nextIndex(state: GameState): number {
  return (state.currentPlayerIndex + 1) % state.players.length;
}

function passTurn(state: GameState, message: string): GameState {
  const next = nextIndex(state);
  return {
    ...state,
    players: clonePlayers(state.players),
    currentPlayerIndex: next,
    phase: 'awaiting_action',
    currentSpinValue: null,
    message: `${message} It's ${state.players[next].name}'s turn.`,
  };
}

function joinGame(state: GameState, playerId: string, playerName: string): GameState {
  const existing = state.players.find((player) => player.id === playerId);
  if (existing) {
    const name = playerName.trim() || existing.name;
    return {
      ...state,
      players: state.players.map((player) =>
        player.id === playerId ? { ...player, name } : player
      ),
    };
  }

  if (state.status !== 'lobby') {
    throw new GameError('This game has already started');
  }
  if (state.players.length >= MAX_PLAYERS) {
    throw new GameError(`This game is full (${MAX_PLAYERS} players max)`);
  }

  const name = playerName.trim() || `Player ${state.players.length + 1}`;
  const player: Player = {
    id: playerId,
    name,
    roundScore: 0,
    totalScore: 0,
    isHost: false,
  };

  return {
    ...state,
    players: [...clonePlayers(state.players), player],
    playerIds: [...state.playerIds, playerId],
    message: `${name} joined the game.`,
  };
}

function setConfig(
  state: GameState,
  playerId: string,
  totalRounds: number,
  selectedCategoryIds: string[]
): GameState {
  requireHost(state, playerId);
  if (state.status !== 'lobby') {
    throw new GameError('Settings can only be changed in the lobby');
  }
  if (!Number.isInteger(totalRounds) || totalRounds < MIN_ROUNDS || totalRounds > MAX_ROUNDS) {
    throw new GameError(`Choose between ${MIN_ROUNDS} and ${MAX_ROUNDS} rounds`);
  }
  if (selectedCategoryIds.length > totalRounds) {
    throw new GameError('Too many categories for the number of rounds');
  }

  return {
    ...state,
    totalRounds,
    selectedCategoryIds: [...selectedCategoryIds],
  };
}

function startGame(state: GameState, playerId: string, deps: EngineDeps): GameState {
  requireHost(state, playerId);
  if (state.status !== 'lobby') {
    throw new GameError('This game has already started');
  }
  if (state.players.length < 1) {
    throw new GameError('At least one player is required');
  }
  if (state.selectedCategoryIds.length !== state.totalRounds) {
    throw new GameError(`Select a category for each of the ${state.totalRounds} rounds`);
  }
  if (!deps.pickPuzzle && state.selectedCategoryIds.some((categoryId) => !getCategory(categoryId))) {
    throw new GameError('One or more categories are invalid');
  }

  return beginRound(
    {
      ...state,
      status: 'playing',
      currentRound: 0,
      currentPlayerIndex: 0,
      winners: [],
    },
    deps
  );
}

function beginRound(state: GameState, deps: EngineDeps): GameState {
  const roundNumber = state.currentRound + 1;
  const categoryId = state.selectedCategoryIds[roundNumber - 1];
  if (!categoryId) {
    throw new GameError('Missing category for this round');
  }

  const pick = (deps.pickPuzzle || defaultPickPuzzle)(categoryId, state.usedPuzzles);
  const starter = state.players[state.currentPlayerIndex];

  return {
    ...state,
    status: 'playing',
    phase: 'awaiting_action',
    currentRound: roundNumber,
    currentPhrase: pick.phrase,
    currentCategory: pick.categoryName,
    usedPuzzles: [...state.usedPuzzles, pick.phrase],
    guessedLetters: [],
    currentSpinValue: null,
    lastSpin: null,
    players: clonePlayers(state.players).map((player) => ({ ...player, roundScore: 0 })),
    message: `Round ${roundNumber} of ${state.totalRounds}: ${pick.categoryName}. ${starter.name}'s turn — spin, buy a vowel for $${VOWEL_COST}, or solve.`,
  };
}

function spinSlot(state: GameState, playerId: string, spin: SpinResult, now: number): GameState {
  const player = requireTurn(state, playerId);
  if (state.phase !== 'awaiting_action') {
    throw new GameError('You already spun — guess a consonant');
  }
  if (!isValidSpin(spin)) {
    throw new GameError('Invalid spin result');
  }

  if (spin.specialValue === 'BANKRUPT') {
    const players = clonePlayers(state.players).map((entry) =>
      entry.id === player.id ? { ...entry, roundScore: 0 } : entry
    );
    return passTurn(
      {
        ...state,
        players,
        lastSpin: spin,
        lastSpinAt: now,
        currentSpinValue: null,
      },
      `${player.name} landed on BANKRUPT and lost their round money!`
    );
  }

  if (spin.specialValue === 'LOSE TURN') {
    return passTurn(
      {
        ...state,
        lastSpin: spin,
        lastSpinAt: now,
        currentSpinValue: null,
      },
      `${player.name} landed on LOSE TURN.`
    );
  }

  return {
    ...state,
    players: clonePlayers(state.players),
    lastSpin: spin,
    lastSpinAt: now,
    currentSpinValue: spin.cashValue,
    phase: 'awaiting_consonant',
    message: `${player.name} spun $${spin.cashValue}. ${player.name} must pick a consonant.`,
  };
}

function guessConsonant(state: GameState, playerId: string, letterInput: string): GameState {
  const player = requireTurn(state, playerId);
  if (state.phase !== 'awaiting_consonant' || state.currentSpinValue == null) {
    throw new GameError('Spin first, then guess a consonant');
  }

  const letter = normalizeLetter(letterInput);
  if (isVowel(letter)) {
    throw new GameError('Vowels must be bought — they are not free');
  }
  if (state.guessedLetters.includes(letter)) {
    throw new GameError(`"${letter.toUpperCase()}" has already been guessed`);
  }

  const count = countLetterInPhrase(state.currentPhrase, letter);
  const guessedLetters = [...state.guessedLetters, letter];

  if (count === 0) {
    return passTurn(
      {
        ...state,
        guessedLetters,
        currentSpinValue: null,
      },
      `${player.name} guessed ${letter.toUpperCase()} — not in the puzzle.`
    );
  }

  const points = count * state.currentSpinValue;
  const players = clonePlayers(state.players).map((entry) =>
    entry.id === player.id ? { ...entry, roundScore: entry.roundScore + points } : entry
  );
  const nextState: GameState = {
    ...state,
    players,
    guessedLetters,
    currentSpinValue: null,
    phase: 'awaiting_action',
    message: `${player.name} found ${count} ${letter.toUpperCase()}${count === 1 ? '' : 's'} for $${points}! Spin again, buy a vowel, or solve.`,
  };

  if (isBoardComplete(nextState.currentPhrase, nextState.guessedLetters)) {
    return completeRound(nextState, player.id, `${player.name} revealed the last letter`);
  }

  return nextState;
}

function buyVowel(state: GameState, playerId: string, letterInput: string): GameState {
  const player = requireTurn(state, playerId);
  if (state.phase !== 'awaiting_action') {
    throw new GameError('Finish your spin before buying a vowel');
  }

  const letter = normalizeLetter(letterInput);
  if (!isVowel(letter)) {
    throw new GameError('You can only buy A, E, I, O, or U');
  }
  if (state.guessedLetters.includes(letter)) {
    throw new GameError(`"${letter.toUpperCase()}" has already been guessed`);
  }
  if (player.roundScore < VOWEL_COST) {
    throw new GameError(`Vowels cost $${VOWEL_COST} in round money`);
  }

  const count = countLetterInPhrase(state.currentPhrase, letter);
  const guessedLetters = [...state.guessedLetters, letter];
  const players = clonePlayers(state.players).map((entry) =>
    entry.id === player.id ? { ...entry, roundScore: entry.roundScore - VOWEL_COST } : entry
  );

  if (count === 0) {
    return passTurn(
      {
        ...state,
        players,
        guessedLetters,
        currentSpinValue: null,
      },
      `${player.name} bought ${letter.toUpperCase()} for $${VOWEL_COST} — not in the puzzle.`
    );
  }

  const nextState: GameState = {
    ...state,
    players,
    guessedLetters,
    currentSpinValue: null,
    phase: 'awaiting_action',
    message: `${player.name} bought ${count} ${letter.toUpperCase()}${count === 1 ? '' : 's'} for $${VOWEL_COST}.`,
  };

  if (isBoardComplete(nextState.currentPhrase, nextState.guessedLetters)) {
    return completeRound(nextState, player.id, `${player.name} revealed the last letter`);
  }

  return nextState;
}

function solvePhrase(state: GameState, playerId: string, guess: string): GameState {
  const player = requireTurn(state, playerId);
  if (state.phase !== 'awaiting_action') {
    throw new GameError('Guess the consonant from your spin before solving');
  }
  if (!guess.trim()) {
    throw new GameError('Enter a solution');
  }

  const correct =
    normalizeSolveGuess(guess) === normalizeSolveGuess(state.currentPhrase);

  if (!correct) {
    return passTurn(
      {
        ...state,
        currentSpinValue: null,
      },
      `${player.name} tried to solve and was incorrect.`
    );
  }

  const revealed = Array.from(new Set([
    ...state.guessedLetters,
    ...uniqueLettersInPhrase(state.currentPhrase),
  ]));

  return completeRound(
    {
      ...state,
      guessedLetters: revealed,
      currentSpinValue: null,
    },
    player.id,
    `${player.name} solved the puzzle`
  );
}

function completeRound(state: GameState, winnerId: string, preface: string): GameState {
  const players = clonePlayers(state.players).map((player) => {
    if (player.id === winnerId) {
      return {
        ...player,
        totalScore: player.totalScore + player.roundScore,
        roundScore: 0,
      };
    }
    return { ...player, roundScore: 0 };
  });
  const banked = (state.players.find((player) => player.id === winnerId)?.roundScore ?? 0);
  const winnerIndex = players.findIndex((player) => player.id === winnerId);

  const revealed = Array.from(new Set([
    ...state.guessedLetters,
    ...uniqueLettersInPhrase(state.currentPhrase),
  ]));

  const base: GameState = {
    ...state,
    players,
    guessedLetters: revealed,
    currentPlayerIndex: winnerIndex >= 0 ? winnerIndex : state.currentPlayerIndex,
    currentSpinValue: null,
    phase: 'round_complete',
    message: `${preface} and banked $${banked}! The puzzle was "${state.currentPhrase}".`,
  };

  if (state.currentRound >= state.totalRounds) {
    return finishGame(base, `${base.message} That's the final round.`);
  }

  return base;
}

function advanceRound(state: GameState, deps: EngineDeps): GameState {
  if (state.status === 'finished' || state.phase === 'game_over') {
    return state;
  }
  if (state.phase !== 'round_complete') {
    // Idempotent: every client may try to advance after a round.
    return state;
  }
  if (state.currentRound >= state.totalRounds) {
    return finishGame(state, state.message);
  }

  return beginRound(
    {
      ...state,
      currentPlayerIndex: nextIndex(state),
    },
    deps
  );
}

function endGame(state: GameState, playerId: string): GameState {
  requireHost(state, playerId);
  if (state.status === 'finished') {
    return state;
  }

  const players = clonePlayers(state.players).map((player) => ({
    ...player,
    totalScore: player.totalScore + player.roundScore,
    roundScore: 0,
  }));

  return finishGame(
    {
      ...state,
      players,
    },
    'The host ended the game.'
  );
}

function finishGame(state: GameState, preface: string): GameState {
  const highest = Math.max(0, ...state.players.map((player) => player.totalScore));
  const winners = state.players.filter((player) => player.totalScore === highest);
  const winnerText =
    winners.length === 1
      ? `${winners[0].name} wins with $${winners[0].totalScore}!`
      : `It's a tie at $${highest}: ${winners.map((player) => player.name).join(', ')}.`;

  return {
    ...state,
    status: 'finished',
    phase: 'game_over',
    currentSpinValue: null,
    winners: clonePlayers(winners),
    message: `${preface} ${winnerText}`.trim(),
  };
}

export function displayCells(phrase: string, guessedLetters: string[]): Array<{
  char: string;
  revealed: boolean;
  isSpace: boolean;
  isPunctuation: boolean;
}> {
  const guessed = new Set(guessedLetters.map((letter) => letter.toLowerCase()));
  return phrase.split('').map((char) => {
    const isSpace = char === ' ';
    const isLetter = /[a-zA-Z]/.test(char);
    const revealed = !isLetter || guessed.has(char.toLowerCase());
    return {
      char,
      revealed,
      isSpace,
      isPunctuation: !isSpace && !isLetter,
    };
  });
}
